import { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "../../../../components/confirmation-dialog";
import PageHeader from "../../../../components/common/PageHeader";
import { Button } from "../../../../components/button";
import type { DataTableSortDirection } from "../../../../components/table";
import { Plus } from "lucide-react";
import { useDeleteSurvey, useGetSurveyList, useSurveyStatistics } from "../hooks";
import type { SurveyListItemForAdminResponse } from "../types/survey.types";
import SurveyListDataTable from "../components/data-table/SurveyListDataTable";
import SurveyListToolbar from "../components/SurveyListToolbar";
import SurveyPageStats from "../components/SurveyPageStats";
import { getSurveyStatus } from "../utils/survey.utils";

type SurveyPageProps = {
  basePath?: string;
  createPath?: string;
};

function SurveyPage({ basePath = "/survey", createPath = "/surveys/create" }: SurveyPageProps) {
  const { t } = useTranslation();
  const {
    surveys,
    loading,
    error,
    search,
    setSearch,
    filters,
    setFilters,
    sorting,
    setSorting,
    page,
    setPage,
    totalCount,
    refreshSurveys,
  } = useGetSurveyList();

  const {
    data: statisticsData,
    loading: statisticsLoading,
    error: statisticsError,
    refresh: refreshStatistics,
  } = useSurveyStatistics();

  const navigate = useNavigate();

  const [surveyToDelete, setSurveyToDelete] = useState<SurveyListItemForAdminResponse | null>(null);
  const { deleteSurvey, isDeleting } = useDeleteSurvey();

  const handleColumnSort = useCallback(
    (
      columnName: "points" | "question_count" | "responses_count",
      direction: DataTableSortDirection | null,
    ) => {
      setPage(1);

      if (direction === null) {
        setSorting({ field: "created_at", direction: "desc", sortKey: "newest_first" });
        return;
      }

      setSorting({ field: columnName, direction });
    },
    [setPage, setSorting],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleDeleteClick = useCallback((survey: SurveyListItemForAdminResponse) => {
    setSurveyToDelete(survey);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setSurveyToDelete(null);
  }, []);

  const handleCreateSurvey = useCallback(() => {
    navigate(createPath);
  }, [createPath, navigate]);

  const handleViewSurvey = useCallback(
    (survey: SurveyListItemForAdminResponse) => {
      navigate(`${basePath}/${survey.id}`);
    },
    [basePath, navigate],
  );

  const handleEditSurvey = useCallback(
    (survey: SurveyListItemForAdminResponse) => {
      navigate(`${basePath}/${survey.id}/edit`);
    },
    [basePath, navigate],
  );

  const handleRefresh = useCallback(async () => {
    await Promise.all([refreshSurveys(), refreshStatistics()]);
  }, [refreshSurveys, refreshStatistics]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!surveyToDelete) return;

    await deleteSurvey(surveyToDelete.id);

    await Promise.all([refreshSurveys(), refreshStatistics()]);
    setSurveyToDelete(null);
  }, [surveyToDelete, refreshSurveys, refreshStatistics, deleteSurvey]);

  const totalPages = Math.ceil(totalCount / 20); // itemsPerPage is 20 in context

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t("survey.management")}
        subtitle={t("survey.subtitle")}
        sticky
        actions={
          <Button
            variant="default"
            className="flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-600"
            onClick={handleCreateSurvey}
          >
            <Plus className="h-4 w-4" />
            {t("survey.add_new_survey")}
          </Button>
        }
      />

      <div className="px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        <SurveyPageStats
          data={statisticsData}
          loading={statisticsLoading}
          error={statisticsError}
          onRetry={() => void refreshStatistics()}
        />
        <SurveyListToolbar
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPage={setPage}
          setSorting={setSorting}
          isLoading={loading}
          onRefresh={handleRefresh}
        />
        <SurveyListDataTable
          surveys={surveys}
          search={search}
          filters={filters}
          setFilters={setFilters}
          loading={loading}
          error={error}
          onRetry={refreshSurveys}
          page={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onRowClick={handleViewSurvey}
          onEditClick={handleEditSurvey}
          onDeleteClick={handleDeleteClick}
          getSurveyStatus={getSurveyStatus}
          sortDirections={{
            points: sorting.field === "points" && !sorting.sortKey ? sorting.direction : null,
            question_count:
              sorting.field === "question_count" && !sorting.sortKey ? sorting.direction : null,
            responses_count:
              sorting.field === "responses_count" && !sorting.sortKey ? sorting.direction : null,
          }}
          onColumnSort={handleColumnSort}
        />
      </div>

      <ConfirmationDialog
        isOpen={Boolean(surveyToDelete)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("survey.delete_survey")}
        description={
          <Trans
            i18nKey="survey.delete_confirm"
            values={{ title: surveyToDelete?.title }}
            components={{
              b: <b className="font-semibold text-foreground" />,
            }}
          />
        }
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        isLoading={isDeleting}
        type="danger"
      />
    </div>
  );
}

export default SurveyPage;
